import {
  Autocomplete,
  Box,
  Chip,
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

export type TextFieldProps =
  | StandardTextFieldProps
  | FilledTextFieldProps
  | OutlinedTextFieldProps;
export interface InputProps extends Omit<TextFieldProps, "size"> {
  size?: "large" | "medium" | "small";
  control?: any;
  name: string;
  label?: React.ReactNode;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  placeholder?: string;
  options?: any[];
  labelMappingKey?: string;
  valueMappingKey?: string;
  /**
   * set to false if fixed input is needed and a size prop can be specified
   */
  isResponsive?: boolean;
  hideHelperBox?: boolean;
  errors?: any;
  chipOnChangeHandler?: (eventValue: any, value: any) => void; // Add this line
  chipValues?: any;
  customStyles?: any;
  validateEmail?: boolean;
  validateDomain?: boolean;
  customValidateFn?: (value: any) => boolean;
  customChip?: (
    option: any,
    index: number,
    getTagProps: any,
  ) => React.ReactNode;
}

const ChipSelectField = ({
  size = "medium",
  control,
  name,
  label,
  placeholder,
  error = false,
  value = [],
  labelMappingKey,
  valueMappingKey,
  errorMessage,
  required = false,
  // variant = "outlined",
  isResponsive = true,
  hideHelperBox = false,
  sx,
  errors,
  chipOnChangeHandler,
  chipValues,
  onBlur,
  customStyles,
  validateEmail = true,
  validateDomain = false,
  customValidateFn,
  options,
  customChip,
}: InputProps) => {
  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {control ? (
        <ControlledCustomInputChip
          name={name}
          control={control}
          label={label}
          placeholder={placeholder}
          labelMappingKey={labelMappingKey}
          valueMappingKey={valueMappingKey}
          errors={errors}
          error={error}
          errorMessage={errorMessage}
          chipOnChangeHandler={chipOnChangeHandler}
          onBlur={onBlur}
          customStyles={customStyles}
          hideHelperBox={hideHelperBox}
          validateEmail={validateEmail}
          validateDomain={validateDomain}
          customValidateFn={customValidateFn}
          options={options}
          customChip={customChip}
        />
      ) : (
        <CustomInputChip
          name={name}
          label={label}
          placeholder={placeholder}
          chipOnChangeHandler={chipOnChangeHandler}
          error={error}
          errorMessage={errorMessage}
          value={value}
          onBlur={onBlur}
          customStyles={customStyles}
          hideHelperBox={hideHelperBox}
          validateEmail={validateEmail}
          validateDomain={validateDomain}
          customValidateFn={customValidateFn}
          labelMappingKey={labelMappingKey}
          valueMappingKey={valueMappingKey}
          options={options}
          customChip={customChip}
        />
      )}
    </Box>
  );
};

export { ChipSelectField };

//uncontrolled component
const CustomInputChip = ({
  size = "medium",
  control,
  name,
  label,
  placeholder,
  error = false,
  value = [],

  labelMappingKey,
  valueMappingKey,
  errorMessage,
  required = false,
  // variant = "outlined",
  isResponsive = true,
  hideHelperBox = false,
  sx,
  chipOnChangeHandler,
  onBlur,
  customStyles,
  validateEmail = true,
  validateDomain = false,
  customValidateFn,
  options = [],
  customChip,
}: InputProps) => {
  const [showValues, setShowValues] = useState<any>(value);
  const [inputValue, setInputValue] = useState<any>("");
  const [errorMessageText, setErrorMessageText] = useState("");
  const theme = useTheme();
  const styles = customStyles || {
    width: "100%",
    fontFamily: theme.typography["body-medium"].fontFamily,
    fontWeight: theme.typography["body-medium"].fontWeight,
    fontSize: theme.typography["body-medium"].fontSize,
    "& .MuiInputBase-input": {
      height: "10px",
    },
    "& .MuiOutlinedInput-root": {
      color: "#1C1E26",
      "& > fieldset": {
        borderColor: "#CCCCCC",
        borderRadius: "4px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#5563F5",
        borderWidth: "1px", // Adjust the border width for the focused state
      },
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      color: "#1C1E26",
      "& > fieldset": { borderColor: "#5563F5" },
    },
    ".Mui-error > fieldset": {
      borderColor: theme.palette.error.main,
    },

    "& .MuiAutocomplete-tag": {
      backgroundColor: "#E7EBF3",
    },

    "& .MuiInputLabel-root": {
      color: "#767B85",
      fontSize: "14px",
      transform: "translate(14px, 12px) scale(1)", // adjest the label position
      "&.Mui-focused": {
        color: theme.palette.purple.main,
        transform: "translate(14px, -9px) scale(.75)",
      },
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
    "& .MuiChip-deleteIcon": {
      width: "16px", // Adjust the width of the delete icon
      height: "16px", // Adjust the height of the delete icon
      // color: "#FF0000", // Change the color of the delete icon
      // "&:hover": {
      //   color: "black", // Color of the delete icon on hover
      // },
    },
  };
  function isEmailValid(email: any) {
    // Regular expression for validating email addresses
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function isDomainValid(domain: any) {
    const domainRegex =
      /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  }

  useEffect(() => {
    if (chipOnChangeHandler) {
      chipOnChangeHandler(null, showValues);
    }
  }, [showValues]);

  useEffect(() => {
    setShowValues(value);
  }, [value]);

  return (
    <Box sx={{ width: "100%" }}>
      <Autocomplete
        multiple
        freeSolo
        id="tags-outlined"
        options={[]}
        // @ts-ignore
        onBlur={onBlur}
        getOptionLabel={(option: any) => option}
        value={showValues}
        onKeyDown={(event: any) => {
          const inputValue = event?.target?.value;
          if (
            event.key === "Backspace" &&
            inputValue === "" &&
            showValues.length > 0
          ) {
            const newValues = [...showValues];
            newValues.pop(); // remove last chip
            setShowValues(newValues);
          }
        }}
        // value={value}
        onChange={(event: any, newValue: any) => {
          // handleInputChange(event, newValue);
          const inputValue = event?.target?.value;
          const isValidEmail = validateEmail
            ? isEmailValid(inputValue)
            : validateDomain
              ? isDomainValid(inputValue)
              : customValidateFn
                ? customValidateFn(inputValue)
                : true;
          if (
            typeof event.target.value === "string" &&
            event.target.value.length > 0
          ) {
            if (isValidEmail) {
              setShowValues(newValue);
              setInputValue("");
              setErrorMessageText("");
            } else if (
              isValidEmail === false &&
              event.target.value !== undefined &&
              event.target.value !== ""
            ) {
              setErrorMessageText(
                validateEmail
                  ? "Please enter a valid email address"
                  : validateDomain
                    ? "Please enter a valid domain name"
                    : customValidateFn
                      ? "Please enter a valid value"
                      : "",
              );
            }
          }
          if (event.target.value == undefined) {
            setShowValues(newValue);
          }
        }}
        filterSelectedOptions
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, value: inputValue }}
              label={label}
              onBlur={onBlur}
              placeholder={placeholder || "type here"}
              sx={styles}
              onChange={(event: any) => {
                const inputValueText = event?.target?.value;
                setInputValue(inputValueText);
                if (errorMessageText) {
                  setErrorMessageText("");
                }
              }}
              error={error || Boolean(errorMessageText)}
              helperText={
                hideHelperBox ? (
                  <></>
                ) : (
                  <Box height={hideHelperBox ? "0px" : "16px"}>
                    <Typography variant="caption-3" color="error">
                      {errorMessage || errorMessageText}
                    </Typography>
                  </Box>
                )
              }
            />
          );
        }}
        renderTags={(value, getTagProps) =>
          (value || []).map((option, index) =>
            customChip ? (
              customChip(option, index, getTagProps)
            ) : (
              // eslint-disable-next-line react/jsx-key
              <Chip
                label={
                  typeof option === "string"
                    ? option
                    : option[labelMappingKey || "label"]
                }
                {...getTagProps({ index })}
                sx={{ height: "20px", marginBottom: "4px" }}
              />
            ),
          )
        }
      />
    </Box>
  );
};

//controlled component with react hook forms
const ControlledCustomInputChip = ({
  size = "medium",
  control, // This prop is required for the Controller component
  name,
  label,
  placeholder,
  error = false,
  value = [],
  labelMappingKey,
  valueMappingKey,
  errorMessage,
  required = false,
  isResponsive = true,
  hideHelperBox = false,
  sx,
  errors,
  customStyles,
  validateEmail = true,
  validateDomain = false,
  customValidateFn,
  options = [],
  customChip,
}: InputProps) => {
  const theme = useTheme();

  const [showValues, setShowValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<any>("");
  const [showError, setShowError] = useState(false);
  useEffect(() => {
    setShowError(false);
  }, []);

  // Custom styles and other functions...
  const styles = customStyles || {
    width: "100%",
    fontFamily: theme.typography["body-medium"].fontFamily,
    fontWeight: theme.typography["body-medium"].fontWeight,
    fontSize: theme.typography["body-medium"].fontSize,
    "& .MuiInputBase-input": {
      height: "10px",
    },
    "& .MuiOutlinedInput-root": {
      color: "#1C1E26",
      // color: showError ? theme.palette.error.main : "#767B85",
      "& > fieldset": {
        borderColor: "#CCCCCC",
        borderRadius: "4px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#5563F5",
        borderWidth: "1px", // Adjust the border width for the focused state
      },
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      color: "#1C1E26",
      "& > fieldset": { borderColor: "#5563F5" },
    },
    ".Mui-error > fieldset": {
      borderColor: theme.palette.error.main,
    },

    "& .MuiAutocomplete-tag": {
      backgroundColor: "#E7EBF3",
    },

    "& .MuiInputLabel-root": {
      color: "#767B85",
      fontSize: "14px",
      transform: "translate(14px, 12px) scale(1)", // adjest the label position
      "&.Mui-focused": {
        color: theme.palette.purple.main,
        transform: "translate(14px, -9px) scale(.75)",
      },
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
    "& .MuiChip-deleteIcon": {
      width: "16px", // Adjust the width of the delete icon
      height: "16px", // Adjust the height of the delete icon
      // color: "#FF0000", // Change the color of the delete icon
      // "&:hover": {
      //   color: "black", // Color of the delete icon on hover
      // },
    },
  };

  function isEmailValid(email: any) {
    // Regular expression for validating email addresses
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function isDomainValid(domain: any) {
    const domainRegex =
      /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  }

  return (
    // <Box sx={{ maxWidth: "100%" }}>
    <Box sx={{ width: "100%" }}>
      <Controller
        control={control} // Pass the control prop to Controller
        name={name}
        defaultValue={[]} // Set default value for the controlled component
        render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            freeSolo
            id="tags-outlined"
            options={options}
            getOptionLabel={(option: any) =>
              typeof option === "string"
                ? option
                : option[`${labelMappingKey || "label"}`]
            }
            value={field.value || []} // Use field.value from Controller, fallback to empty array
            onKeyDown={(event: any) => {
              const inputValue = event?.target?.value;
              if (
                event.key === "Backspace" &&
                inputValue === "" &&
                (field.value || []).length > 0
              ) {
                const newValues = [...(field.value || [])];
                newValues.pop(); // remove last chip
                field.onChange(newValues);
              }
            }}
            onBlur={(event: any) => {
              if (event.target.value) {
                field.onChange([...(field.value || []), event.target.value]);
              }
              setInputValue("");
            }}
            clearOnEscape
            onChange={(event: any, newValue: any) => {
              const inputValue = event?.target?.value;
              // setInputValue(inputValue);

              // Handle when user types and presses comma/space (string value exists)
              if (
                typeof event.target.value === "string" &&
                event.target.value.length > 0
              ) {
                const isValidEmail = validateEmail
                  ? isEmailValid(inputValue)
                  : validateDomain
                    ? isDomainValid(inputValue)
                    : customValidateFn
                      ? customValidateFn(inputValue)
                      : true;
                if (isValidEmail) {
                  field.onChange(newValue);
                  setInputValue("");
                  setShowError(false);
                } else {
                  setShowError(true);
                }
              }
              // Handle when user presses Enter or removes a chip (value is undefined)
              else if (event.target.value === undefined) {
                // Check if a new email was added (newValue has more items than current value)
                const currentValue = field.value || [];
                if (newValue.length > currentValue.length) {
                  // A new email was added - validate it
                  const newEmail = newValue[newValue.length - 1];
                  const isValidEmail = validateEmail
                    ? isEmailValid(newEmail)
                    : validateDomain
                      ? isDomainValid(newEmail)
                      : customValidateFn
                        ? customValidateFn(newEmail)
                        : true;

                  if (isValidEmail) {
                    field.onChange(newValue);
                    setInputValue("");
                    setShowError(false);
                  } else {
                    // Don't add invalid email
                    setShowError(true);
                  }
                } else {
                  // A chip was removed (delete icon clicked)
                  field.onChange(newValue);
                  setShowError(false);
                }
              }
            }}
            filterSelectedOptions
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  inputProps={{ ...params.inputProps, value: inputValue }}
                  label={label}
                  placeholder={placeholder}
                  sx={styles}
                  onChange={(event: any) => {
                    const inputValueText = event?.target?.value;
                    setInputValue(inputValueText);
                    if (showError) {
                      setShowError(false);
                    }
                  }}
                  error={error}
                  helperText={
                    hideHelperBox ? (
                      <></>
                    ) : (
                      <Box height={hideHelperBox ? "0px" : "16px"}>
                        <Typography variant="caption-3" color="error">
                          {(errorMessage || showError) &&
                            (validateEmail
                              ? "Enter a valid email address"
                              : validateDomain
                                ? "Enter a valid domain name"
                                : customValidateFn
                                  ? "Please enter a valid value"
                                  : "")}
                        </Typography>
                      </Box>
                    )
                  }
                />
              );
            }}
            renderTags={(value, getTagProps) =>
              (value || []).map((option, index) =>
                customChip ? (
                  customChip(option, index, getTagProps)
                ) : (
                  // eslint-disable-next-line react/jsx-key
                  <Chip
                    label={
                      typeof option === "string"
                        ? option
                        : option[labelMappingKey || "label"]
                    }
                    {...getTagProps({ index })}
                    sx={{
                      height: "20px",
                      marginBottom: "4px",
                      fontFamily: theme.typography["body-small"].fontFamily,
                      fontSize: theme.typography["body-small"].fontSize,
                    }}
                  />
                ),
              )
            }
          />
        )}
      />
    </Box>
  );
};
